import { useState, useEffect } from 'react'
import { useWedding } from '../../context/WeddingContext'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { Copy, Check, Download, RefreshCw, Crown, Heart, Trash2, Edit2 } from 'lucide-react'
import PageHeader from '../../components/PageHeader'
import { celebrateOnce } from '../../lib/celebrate'

const TEMPLATES = {
  vow_bride: {
    label:"Bride's Vows", icon:'💍',
    fields:[
      {key:'partner',label:"Partner's first name",placeholder:'e.g. Chukwuemeka'},
      {key:'how_met',label:'How did you meet?',placeholder:"e.g. at a mutual friend's birthday in Lagos"},
      {key:'quality1',label:'His best quality',placeholder:'e.g. his patience and gentle spirit'},
      {key:'quality2',label:'A tender memory',placeholder:'e.g. when he drove 3 hours just to bring me jollof rice'},
      {key:'promise1',label:'First promise',placeholder:'e.g. to always be your safe place'},
      {key:'promise2',label:'Second promise',placeholder:'e.g. to choose you every single day'},
      {key:'closing',label:'Closing endearment',placeholder:'e.g. my best friend, my home, my forever'},
    ],
    generate:(f)=>`${f.partner}, from the moment we met ${f.how_met}, I knew there was something different about you.\n\nIt is ${f.quality1} that drew me in, and it is memories like ${f.quality2} that remind me why I am the luckiest person in this room today.\n\nBefore God, our families, and everyone who loves us, I make you these promises:\n\nI promise ${f.promise1}. I promise ${f.promise2}.\n\nYou are ${f.closing}. And I choose you — today, tomorrow, and every day after.`
  },
  vow_groom: {
    label:"Groom's Vows", icon:'🤵',
    fields:[
      {key:'partner',label:"Partner's first name",placeholder:'e.g. Adaeze'},
      {key:'how_met',label:'How did you meet?',placeholder:'e.g. at church during a youth retreat'},
      {key:'quality1',label:'Her best quality',placeholder:'e.g. her kindness and infectious laughter'},
      {key:'memory',label:'A meaningful memory',placeholder:"e.g. the night we stayed up talking until 4am"},
      {key:'promise1',label:'First promise',placeholder:'e.g. to love you without conditions'},
      {key:'promise2',label:'Second promise',placeholder:'e.g. to build a home where you always feel cherished'},
      {key:'closing',label:'Closing words',placeholder:'e.g. my answered prayer, my greatest adventure'},
    ],
    generate:(f)=>`${f.partner}, standing here today, I realize this moment has been coming since ${f.how_met}.\n\nI fell in love with ${f.quality1}. I knew you were the one ${f.memory}.\n\nI make you these vows in front of everyone who matters most to us:\n\nI promise ${f.promise1}. I promise ${f.promise2}.\n\nYou are ${f.closing}. And I am so grateful God wrote your name into my story.`
  },
  speech_bestman: {
    label:"Best Man's Speech", icon:'🎤',
    fields:[
      {key:'groom',label:"Groom's name",placeholder:'e.g. Emeka'},
      {key:'bride',label:"Bride's name",placeholder:'e.g. Adaeze'},
      {key:'how_know',label:'How you know the groom',placeholder:"e.g. we've been friends since secondary school in 2008"},
      {key:'funny_story',label:'A funny (clean!) story',placeholder:"e.g. the time he showed up 2 hours late to his own surprise party"},
      {key:'when_knew',label:'When you knew she was special',placeholder:'e.g. the first time he cancelled our FIFA night to call her instead'},
      {key:'toast',label:'Your toast',placeholder:'e.g. a lifetime of laughter and may the WiFi always work'},
    ],
    generate:(f)=>`Good evening, everyone. For those who don't know me — I'm the best man. I'll keep this appropriate.\n\n${f.groom} and I have known each other ${f.how_know}. In that time, I have seen him do many things — like ${f.funny_story}.\n\nBut I knew everything changed when ${f.when_knew}. That's when I knew — he'd finally met his match.\n\n${f.bride}, thank you for making my best friend the best version of himself.\n\nPlease raise your glasses. To ${f.groom} and ${f.bride}: ${f.toast}. Cheers!`
  },
  speech_moh: {
    label:"Maid of Honour", icon:'💐',
    fields:[
      {key:'bride',label:"Bride's name",placeholder:'e.g. Adaeze'},
      {key:'groom',label:"Groom's name",placeholder:'e.g. Emeka'},
      {key:'how_know',label:'How you know the bride',placeholder:"e.g. we've been inseparable since UNILAG"},
      {key:'quality',label:"Bride's best quality",placeholder:'e.g. she has the biggest heart of anyone I know'},
      {key:'memory',label:'A favourite memory',placeholder:'e.g. our Abuja trip where everything went wrong but we laughed'},
      {key:'groom_moment',label:'When you approved of the groom',placeholder:'e.g. when he remembered she was lactose intolerant on their third date'},
      {key:'toast',label:'Your blessing',placeholder:'e.g. may your love grow deeper every year'},
    ],
    generate:(f)=>`Good evening, everyone. For anyone who doesn't know me — I'm the Maid of Honour, which means I've been ${f.bride}'s partner in crime ${f.how_know}.\n\n${f.bride} is many things. But above all else, ${f.quality}. One of my favourite memories is ${f.memory}.\n\nWhen ${f.groom} came along, I knew he was the one ${f.groom_moment}. That's the kind of love she deserves.\n\n${f.groom}, you are gaining a remarkable woman.\n\nTo ${f.bride} and ${f.groom}: ${f.toast}. Cheers!`
  },
}

export default function VowWriterPage() {
  const { wedding, isPremium } = useWedding()
  const navigate      = useNavigate()
  const [selected, setSelected] = useState('vow_bride')
  const [fields, setFields]     = useState({})
  const [output, setOutput]     = useState('')
  const [copied, setCopied]     = useState(false)
  const [saving, setSaving]     = useState(false)
  const [savedOk, setSavedOk]   = useState(false)
  const [drafts, setDrafts]     = useState({}) // { key: { text, label, savedAt } }
  const [editingDraft, setEditingDraft] = useState(null)

  // Load saved drafts from DB on mount
  useEffect(() => {
    if (!wedding?.id) return
    supabase.from('weddings').select('vow_drafts').eq('id', wedding.id).maybeSingle()
      .then(({ data }) => { if (data?.vow_drafts) setDrafts(data.vow_drafts) })
  }, [wedding?.id])

  const template = TEMPLATES[selected]

  function generate() {
    const filled = { ...fields }
    template.fields.forEach(f => { if (!filled[f.key]) filled[f.key] = `[${f.label}]` })
    setOutput(template.generate(filled))
  }

  async function saveDraft() {
    if (!output || !wedding?.id) return
    setSaving(true)
    const newDrafts = {
      ...drafts,
      [selected]: { text: output, label: template.label, icon: template.icon, savedAt: new Date().toISOString() }
    }
    await supabase.from('weddings').update({ vow_drafts: newDrafts }).eq('id', wedding.id)
    const isFirstEver = Object.keys(drafts).length === 0
    setDrafts(newDrafts)
    setSaving(false); setSavedOk(true); setTimeout(() => setSavedOk(false), 2500)
    if (isFirstEver) {
      celebrateOnce(`vows:${wedding.id}`, { title: 'Your first words, saved ✦', note: 'The vows that begin your forever are safe with us.' })
    }
  }

  async function deleteDraft(key) {
    const newDrafts = { ...drafts }
    delete newDrafts[key]
    await supabase.from('weddings').update({ vow_drafts: newDrafts }).eq('id', wedding.id)
    setDrafts(newDrafts)
    if (editingDraft === key) setEditingDraft(null)
  }

  async function updateDraftText(key, text) {
    const newDrafts = { ...drafts, [key]: { ...drafts[key], text } }
    await supabase.from('weddings').update({ vow_drafts: newDrafts }).eq('id', wedding.id)
    setDrafts(newDrafts)
  }

  function copyText() { navigator.clipboard.writeText(output); setCopied(true); setTimeout(() => setCopied(false), 2000) }
  function downloadTxt() {
    const blob = new Blob([output], { type:'text/plain' })
    const url  = URL.createObjectURL(blob)
    const a = document.createElement('a'); a.href=url; a.download=`${selected}.txt`; document.body.appendChild(a); a.click(); document.body.removeChild(a)
  }

  const savedKeys = Object.keys(drafts)

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'1.5rem' }}>
      <PageHeader title="Vow & Speech Writer" subtitle="The words that start your forever. Fill in a few details and we'll shape a beautiful draft to make your own." />

      {!isPremium && (
        <div style={{ background:'linear-gradient(135deg,rgba(196,130,106,0.08),rgba(155,175,155,0.08))', border:'1.5px solid rgba(196,130,106,0.25)', borderRadius:'1.25rem', padding:'1.5rem', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'1rem', flexWrap:'wrap' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
            <Crown size={22} color="#C4826A"/>
            <div>
              <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.9rem', color:'#1A1A1A', margin:'0 0 0.2rem' }}>Vow & Speech Writer is Premium</p>
              <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', color:'#6B6B6B', margin:0 }}>Upgrade to generate and save personalised vows and speeches.</p>
            </div>
          </div>
          <button className="btn-primary" onClick={()=>navigate('/pricing')} style={{ flexShrink:0 }}>Upgrade</button>
        </div>
      )}

      {/* Saved Drafts */}
      {isPremium && savedKeys.length > 0 && (
        <div>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.78rem', color:'#6B6B6B', textTransform:'uppercase', letterSpacing:'0.06em', margin:'0 0 0.625rem' }}>Saved Drafts</p>
          <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
            {savedKeys.map(key => {
              const d = drafts[key]
              const isEditing = editingDraft === key
              return (
                <div key={key} style={{ background:'white', border:`1.5px solid ${isEditing?'#C4826A':'#E5E0D8'}`, borderRadius:'1rem', padding:'1rem' }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: isEditing?'0.75rem':0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
                      <span style={{ fontSize:'1.1rem' }}>{d.icon}</span>
                      <div>
                        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.85rem', color:'#1A1A1A', margin:0 }}>{d.label}</p>
                        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.65rem', color:'#BCBCBC', margin:0 }}>Saved {new Date(d.savedAt).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:'0.4rem' }}>
                      <button onClick={()=>setEditingDraft(isEditing?null:key)} style={{ background:'none', border:'none', cursor:'pointer', color:'#C4826A', padding:'4px' }}><Edit2 size={15}/></button>
                      <button onClick={()=>deleteDraft(key)} style={{ background:'none', border:'none', cursor:'pointer', color:'#BCBCBC', padding:'4px' }}><Trash2 size={15}/></button>
                    </div>
                  </div>
                  {isEditing && (
                    <div>
                      <textarea value={d.text} onChange={e=>setDrafts(prev=>({...prev,[key]:{...prev[key],text:e.target.value}}))}
                        rows={10} style={{ width:'100%', fontFamily:'Gibs, serif', fontSize:'0.875rem', padding:'1rem', border:'1.5px solid #E5E0D8', borderRadius:'0.75rem', outline:'none', resize:'vertical', lineHeight:1.9, boxSizing:'border-box' }}/>
                      <div style={{ display:'flex', gap:'0.5rem', marginTop:'0.5rem', flexWrap:'wrap' }}>
                        <button onClick={()=>updateDraftText(key,d.text)} className="btn-primary" style={{ display:'flex', alignItems:'center', gap:'0.35rem', padding:'0.5rem 1rem', fontSize:'0.78rem' }}><Check size={13}/> Save Changes</button>
                        <button onClick={()=>{navigator.clipboard.writeText(d.text)}} className="btn-secondary" style={{ padding:'0.5rem 1rem', fontSize:'0.78rem' }}>Copy</button>
                        <button onClick={()=>{ const blob=new Blob([d.text],{type:'text/plain'}); const url=URL.createObjectURL(blob); const a=document.createElement('a'); a.href=url; a.download=`${key}.txt`; document.body.appendChild(a); a.click(); document.body.removeChild(a) }} className="btn-secondary" style={{ padding:'0.5rem 1rem', fontSize:'0.78rem', display:'flex', alignItems:'center', gap:'0.35rem' }}><Download size={13}/> Download</button>
                        <button onClick={()=>setEditingDraft(null)} style={{ background:'none', border:'none', cursor:'pointer', color:'#BCBCBC', marginLeft:'auto' }}><RefreshCw size={14}/></button>
                      </div>
                    </div>
                  )}
                  {!isEditing && (
                    <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.73rem', color:'#6B6B6B', margin:'0.5rem 0 0', lineHeight:1.6, overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical' }}>
                      {(d.text||'').slice(0, 120)}…
                    </p>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Template selector */}
      {isPremium && (
        <>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:'0.625rem' }}>
            {Object.entries(TEMPLATES).map(([key,t])=>(
              <button key={key} onClick={()=>{ setSelected(key); setOutput(''); setFields({}) }}
                style={{ display:'flex', alignItems:'center', gap:'0.625rem', padding:'0.875rem 1rem', border:`2px solid ${selected===key?'#C4826A':'#E5E0D8'}`, background:selected===key?'rgba(196,130,106,0.06)':'white', borderRadius:'0.875rem', cursor:'pointer', textAlign:'left', transition:'all 0.15s' }}>
                <span style={{ fontSize:'1.2rem' }}>{t.icon}</span>
                <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', fontWeight:selected===key?700:500, color:selected===key?'#C4826A':'#1A1A1A' }}>{t.label}</span>
              </button>
            ))}
          </div>

          <div style={{ display:'flex', gap:'1.5rem', flexWrap:'wrap', alignItems:'flex-start' }}>
            {/* Form */}
            <div style={{ flex:'1 1 280px', display:'flex', flexDirection:'column', gap:'0.875rem' }}>
              <h3 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1rem', color:'#1A1A1A', margin:'0 0 0.25rem' }}>{template.icon} {template.label}</h3>
              {template.fields.map(f=>(
                <div key={f.key}>
                  <label className="label">{f.label}</label>
                  <input className="input-field" placeholder={f.placeholder} value={fields[f.key]||''} onChange={e=>setFields(prev=>({...prev,[f.key]:e.target.value}))}/>
                </div>
              ))}
              <div style={{ display:'flex', gap:'0.625rem' }}>
                <button className="btn-primary" onClick={generate} style={{ flex:1, justifyContent:'center', display:'flex', alignItems:'center', gap:'0.4rem' }}>
                  <Heart size={14}/> Generate Draft
                </button>
                {output && <button className="btn-secondary" onClick={()=>{ setOutput(''); setFields({}) }} style={{ display:'flex', alignItems:'center', gap:'0.4rem' }}><RefreshCw size={14}/> Reset</button>}
              </div>
            </div>

            {/* Output */}
            <div style={{ flex:'1 1 300px' }}>
              {output ? (
                <div style={{ display:'flex', flexDirection:'column', gap:'0.875rem' }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'0.5rem' }}>
                    <h3 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1rem', color:'#1A1A1A', margin:0 }}>Your Draft</h3>
                    <div style={{ display:'flex', gap:'0.4rem', flexWrap:'wrap' }}>
                      <button onClick={saveDraft} className="btn-primary" style={{ display:'flex', alignItems:'center', gap:'0.35rem', fontSize:'0.78rem', padding:'0.5rem 0.875rem' }}>
                        <Check size={13}/> {savedOk?'Saved!':saving?'Saving…':'Save Draft'}
                      </button>
                      <button onClick={copyText} className="btn-secondary" style={{ display:'flex', alignItems:'center', gap:'0.35rem', fontSize:'0.78rem', padding:'0.5rem 0.875rem' }}>
                        {copied?<Check size={13}/>:<Copy size={13}/>} {copied?'Copied!':'Copy'}
                      </button>
                      <button onClick={downloadTxt} className="btn-secondary" style={{ display:'flex', alignItems:'center', gap:'0.35rem', fontSize:'0.78rem', padding:'0.5rem 0.875rem' }}>
                        <Download size={13}/> Download
                      </button>
                    </div>
                  </div>
                  <textarea value={output} onChange={e=>setOutput(e.target.value)} rows={16}
                    style={{ width:'100%', fontFamily:'Gibs, serif', fontSize:'0.875rem', padding:'1.25rem', border:'1.5px solid #E5E0D8', borderRadius:'1rem', outline:'none', resize:'vertical', lineHeight:1.9, color:'#1A1A1A', background:'white', boxSizing:'border-box' }}/>
                  <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#BCBCBC', margin:0 }}>✦ Edit freely — the most meaningful vows are in your own words.</p>
                </div>
              ) : (
                <div style={{ background:'#FAFAF8', border:'1.5px dashed #E5E0D8', borderRadius:'1rem', padding:'3rem 2rem', textAlign:'center', minHeight:300, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
                  <Heart size={32} color="#E5E0D8" style={{ marginBottom:'1rem' }}/>
                  <p style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1rem', color:'#BCBCBC', margin:'0 0 0.5rem' }}>Your draft will appear here</p>
                  <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', color:'#BCBCBC', margin:0 }}>Fill in the fields and click Generate</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
